import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, UserButton } from "@clerk/react";
import { makeApi } from "@/lib/api";
import {
  LayoutDashboard,
  BookOpen,
  GraduationCap,
  Dumbbell,
  Beaker,
  BarChart3,
  CreditCard,
  Settings,
  Shield,
  Menu,
  Lightbulb,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
  { to: "/app/my-exams", label: "My Exams", icon: GraduationCap, id: "nav-my-exams" },
  { to: "/app/my-subjects", label: "My Subjects", icon: BookOpen, id: "nav-my-subjects" },
  { to: "/app/practice", label: "Practice", icon: Dumbbell, id: "nav-practice" },
  { to: "/app/mock-tests", label: "Mock Tests", icon: Beaker, id: "nav-mock-tests" },
  { to: "/app/results", label: "Results & Progress", icon: BarChart3, id: "nav-results" },
  { to: "/app/insights", label: "Performance Insights", icon: Lightbulb, id: "nav-insights" },
  { to: "/app/subscription", label: "Subscription", icon: CreditCard, id: "nav-subscription" },
  { to: "/app/account", label: "Account Settings", icon: Settings, id: "nav-account" },
];

export default function StudentLayout() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const api = makeApi(getToken);
    api
      .get("/me")
      .then((r) => {
        setMe(r.data);
        if (!r.data.onboarded) navigate("/onboarding", { replace: true });
      })
      .catch(() => {});
  }, [isLoaded, isSignedIn, getToken, navigate]);

  return (
    <div className="min-h-screen flex bg-[#FAFAFC]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-white border-r border-slate-200 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-blue-600 text-white grid place-items-center font-bold font-display">J</div>
            <span className="font-display font-bold text-base">JRFHITS<span className="text-blue-600">.COM</span></span>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
          {["admin", "reviewer"].includes(me?.role) && (
            <NavLink
              to={me.role === "admin" ? "/admin" : "/admin/question-bank"}
              onClick={() => setOpen(false)}
              data-testid="nav-admin"
              className="mt-6 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm bg-slate-900 text-white hover:bg-slate-800"
            >
              <Shield className="h-4 w-4" /> Admin panel
            </NavLink>
          )}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="text-sm text-slate-500 hidden sm:block">
              {me ? `Welcome, ${me.first_name || me.email || "student"}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100" data-testid="free-attempts-badge">
              {!me ? "…" : me.subscription_active ? "Subscribed" : `${me.free_attempts_remaining} free attempt${me.free_attempts_remaining === 1 ? "" : "s"}`}
            </span>
            <UserButton />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-10">
          <Outlet context={{ me, setMe }} />
        </main>
      </div>

      {open && (
        <button
          className="fixed inset-0 bg-slate-900/30 z-30 lg:hidden"
          onClick={() => setOpen(false)}
          aria-label="Close overlay"
        />
      )}
    </div>
  );
}
